import React, { useMemo, useState } from "react";
import Table from "@/components/Table/table";
import { useColumnDistribution } from "./DIstributionHeader";
import { useGetDistributionByCaseIDQuery } from "@/services/distribution/distributionReducers";
import { useNavigate, useParams } from "react-router-dom";
import { MotifButton, MotifProgressLoader } from "@ey-xd/motif-react";
import { useCustomCellRenderers } from "./constants/customCellRender";
import DeleteDistribution from "./DeleteDistribution";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useCase } from "@/features/case/use-case";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { commonFormateName, havingAddPermission } from "@/utility/common";
import {
  CREATE_BUTTON_VARIANT,
  EXPORT_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";

const TitleComplement = (
  Distribution: string[],
  setDeleteErrorMessage: any,
  paginationObj: any
) => {
  const { caseId } = useParams<{
    caseId: string;
  }>();
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );
  const { t } = useTranslation();
  const [exportDocument, { isFetching }] = useLazyExportDocumentQuery();
  function handleExport() {
    exportDocument({
      url: "/Distribution/ExportDistributionList",
      fileName: caseShortName
        ? commonFormateName(caseShortName, "Distribution", abbreviation)
        : "",
      ...paginationObj,
    })
      .then((response) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response?.error?.data as string);
            setDeleteErrorMessage(errObj?.message || errObj.title);
          } catch (error) {
            setDeleteErrorMessage("Something went wrong!!");
          }
        }
      })
      .catch((error: any) => {
        setDeleteErrorMessage("Something went wrong!!");
      });
  }

  const navigate = useNavigate();
  return (
    <div style={{rowGap:"5px"}} className="display-flex align-items-center justify-content-between">
      
      <MotifButton
        onClick={() => handleExport()}
        loading={isFetching}
        className="mar-r-10"
        variant={EXPORT_BUTTON_VARIANT}
      >
        {t("global.globalActions.export")}
      </MotifButton>
      {havingAddPermission(Distribution) ? (
        <MotifButton
          size="small"
          type="submit"
          variant={CREATE_BUTTON_VARIANT}
          onClick={() => {
            navigate(`/case/${caseId}/distribution/create`);
          }}
        >
          {t("pages.distributions.distribution.addNewDistribution")}{" "}
        </MotifButton>
      ) : (
        <></>
      )}
    </div>
  );
};

interface DistributionData {
  id: number;
  name: string;
  value: number;
}

export default function Distribution() {
  const { caseId } = useParams<{
    caseId: string;
  }>();
  const { t } = useTranslation();
  const caseData = useCase();
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const permissions = useSelector((state: any) => state.permissions);
  const retainState = useSelector(
    (e: any) => e.retainState.distributionListTabState
  );
  const dispatch = useDispatch();
  const { Distribution } = permissions;
  let tableLoadingData = customTableLoadingData;
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "distributionListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }

  const columnDistribution = useColumnDistribution(caseData?.abbreviation);
  const {
    data,
    isLoading,
    isFetching,
    error,
    status,
    refetch: distributionRefetch,
  } = useGetDistributionByCaseIDQuery(paginationObj);
  const [showDeleteClassPopup, setShowDeleteClassPopup] = useState(false);
  const [distributionData, setDistributionData] =
    useState<DistributionData | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const caseRenderers = useCustomCellRenderers({
    setDistributionData: setDistributionData,
    setShowDeleteClassPopup: setShowDeleteClassPopup,
    Distribution: Distribution,
    duplicateBtnLabel: t("pages.distributions.distribution.duplicate"),
  });

  return (
    <>
      <div className="mar-b-20">
        {deleteErrorMessage && (
          <NotificationGrid
            show={true}
            message={deleteErrorMessage}
            type="error"
            onClose={() => setDeleteErrorMessage("")}
          />
        )}
      </div>
      <div>
        {isLoading ? (
          <MotifProgressLoader show variant="default" className="mar-b-20" />
        ) : (
          <div className="mar-b-20">
            <Table
              columnDefs={columnDistribution}
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              paginationObj={paginationObj}
              cellRenderers={isFetching ? [] : data ? data.results : []}
              TitleComplement={() =>
                TitleComplement(
                  Distribution,
                  setDeleteErrorMessage,
                  paginationObj
                )
              }
              title={"pages.distributions.distribution.title"}
              description={"pages.distributions.distribution.tableDescription"}
              pagination={false}
              caseRenderers={caseRenderers}
              overlayNoRowsTemplate={
                isFetching
                  ? t(tableLoadingData)
                  : error
                  ? t("global.globalNotifications.wentWrong")
                  : t("global.globalNotifications.noRecords")
              }
            />
            <CustomPagination
              apiData={data}
              apiStatus={status}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!data?.hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "distributionListTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "distributionListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "distributionListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          </div>
        )}
      </div>
      {showDeleteClassPopup && (
        <DeleteDistribution
          show={true}
          setShow={setShowDeleteClassPopup}
          id={distributionData?.id?.toString()}
          setDeleteErrorMessage={setDeleteErrorMessage}
          refetch={distributionRefetch}
          entityName={distributionData?.name}
        />
      )}
    </>
  );
}
